import React, { useState, useMemo } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { PlusCircleIcon, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { calculateTotalAvailable } from "../../utils/billingCalculations";

const PaymentModal = ({ show, onHide, onAddPayment, onDeletePayment, userBilling, isUserPage = false, isAdmin = false, adminUser }) => {
  const { t } = useTranslation();
  // Default to credit when not on users page, deposit when on users page
  const [paymentType, setPaymentType] = useState(isUserPage ? "deposit" : "credit");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentPercentage, setPaymentPercentage] = useState("40");
  const [paymentMethod, setPaymentMethod] = useState("tipsterpage");
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentComment, setPaymentComment] = useState("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);

  const maxDepositAmount = useMemo(() => {
    return calculateTotalAvailable(adminUser?.billing?.payments);
  }, [adminUser?.billing?.payments]);

  const handleAddPayment = () => {
    const formattedValues = {
      amount: parseFloat(paymentAmount) || 0,
      type: paymentType,
      ...(paymentType === 'credit' && {
        rate: parseInt(paymentPercentage, 10) || 40,
        method: String(paymentMethod || 'tipsterpage'),
        reference: String(paymentReference || ''),
        comment: String(paymentComment || '')
      })
    };

    // Basic validation
    if (formattedValues.amount <= 0) {
      return; // Could add error handling here
    }

    onAddPayment(formattedValues);
    onHide();
    
    // Reset form with defaults
    setPaymentAmount("");
    setPaymentPercentage("40");
    setPaymentMethod("tipsterpage");
    setPaymentReference("");
    setPaymentComment("");
    setPaymentType(isUserPage ? "deposit" : "credit");
  };

  const handleDeleteClick = (payment) => {
    setPaymentToDelete(payment);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = () => {
    if (paymentToDelete && onDeletePayment) {
      onDeletePayment(paymentToDelete);
      setShowDeleteConfirmation(false);
      setPaymentToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
    setPaymentToDelete(null);
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t("addCreditPayment")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Payment History Section */}
            <div className="w-full lg:w-1/2 lg:border-r border-[#FFFFFF]/20 lg:pr-6">
              <h3 className="text-lg font-semibold text-white mb-4">{t("paymentHistory")}</h3>
              <div className="space-y-4 max-h-[200px] lg:max-h-[400px] overflow-y-auto">
                {userBilling?.payments?.length > 0 ? (
                  userBilling?.payments
                    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                    .map((payment, index) => (
                      <div key={index} className="bg-[#2A3C29] p-4 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <span className={`font-semibold ${payment.amount < 0 ? 'text-red-400' : 'text-[#E8FC8B]'}`}>
                            {payment.type === 'withdrawal' ? '-' : ''}{Math.abs(payment.amount).toFixed(2)}€
                          </span>
                          <span className="text-[#FFFFFF] text-sm">
                            {new Date(payment.timestamp).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="text-[#FFFFFF] text-sm">
                          {t("type")}: {t(payment.type)}
                        </div>
                        {payment.method && (
                          <div className="text-[#FFFFFF] text-sm">
                            {t("paymentMethod")}: {t(payment.method)}
                          </div>
                        )}
                        {payment.reference && (
                          <div className="text-[#FFFFFF] text-sm">
                            {t("reference")}: {payment.reference}
                          </div>
                        )}
                        {payment.comment && (
                          <div className="text-[#FFFFFF] text-sm">
                            {t("comment")}: {payment.comment}
                          </div>
                        )}
                        {payment.rate && (
                          <div className="text-[#FFFFFF] text-sm">
                            {t("rateOverProfit")}: {payment.rate}%
                          </div>
                        )}
                        {isAdmin && onDeletePayment && (
                          <div className="flex justify-end mt-3">
                            <button
                              onClick={() => handleDeleteClick(payment)}
                              className="flex items-center space-x-1 text-red-400 hover:text-red-300 text-sm transition-colors duration-200"
                            >
                              <Trash2 size={14} />
                              <span>{t("deletePayment")}</span>
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                ) : (
                  <div className="text-[#FFFFFF] text-center py-4">{t("noPaymentHistory")}</div>
                )}
              </div>
            </div>

            {/* New Payment Form Section */}
            <div className="w-full lg:w-1/2">
              <h3 className="text-lg font-semibold text-white mb-4">{t("newPayment")}</h3>
              <div className="space-y-4">
                {isUserPage ? (
                  <div>
                    <label className="block text-sm font-medium mb-1 text-white">{t("type")}</label>
                    <div className="flex gap-2">
                      <button
                        className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                          paymentType === 'deposit'
                            ? 'bg-[#E8FC8B] text-[#1B2D1A]'
                            : 'bg-[#2A3C29] text-[#FFFFFF] hover:bg-[#3A4C39]'
                        }`}
                        onClick={() => setPaymentType('deposit')}
                      >
                        {t("deposit")}
                      </button>
                      {userBilling?.maxWithdrawalAmount < 0 && (
                        <button
                          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                            paymentType === 'withdrawal'
                              ? 'bg-[#E8FC8B] text-[#1B2D1A]'
                              : 'bg-[#2A3C29] text-[#FFFFFF] hover:bg-[#3A4C39]'
                          }`}
                          onClick={() => setPaymentType('withdrawal')}
                        >
                          {t("withdrawal")}
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                            paymentType === 'credit'
                              ? 'bg-[#E8FC8B] text-[#1B2D1A]'
                              : 'bg-[#2A3C29] text-[#FFFFFF] hover:bg-[#3A4C39]'
                          }`}
                          onClick={() => setPaymentType('credit')}
                        >
                          {t("credit")}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  // When not on users page, only show credit fields
                  <div>
                    <input type="hidden" value="credit" />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-1 text-white">
                    {paymentType === 'withdrawal' ? t("withdrawal") : t("amountPaid")}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={paymentAmount}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                      onChange={(e) => setPaymentAmount(e.target.value)}
                    />
                    <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">€</span>
                  </div>
                  {paymentType === 'deposit' && (
                    <div className="bg-[#2A3C29] p-2 rounded-md mt-2">
                      <div className="text-sm font-medium text-[#E8FC8B]">
                        {t("totalAvailable")}: {maxDepositAmount.toFixed(2)}€
                      </div>
                    </div>
                  )}
                  {paymentType === 'withdrawal' && userBilling?.maxWithdrawalAmount < 0 && (
                    <div className="bg-[#2A3C29] p-2 rounded-md mt-2">
                      <div className="text-sm font-medium text-[#E8FC8B]">
                        {t("totalAvailable")}: {Math.abs(userBilling?.maxWithdrawalAmount).toFixed(2)}€
                      </div>
                    </div>
                  )}
                </div>

                {(!isUserPage || (isAdmin && paymentType === 'credit')) && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-white">{t("profitPercentage")}</label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          placeholder="30"
                          value={paymentPercentage}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                          onChange={(e) => setPaymentPercentage(e.target.value)}
                        />
                        <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">%</span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1 text-white">{t("paymentMethod")}</label>
                      <select
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                      >
                        <option value="" disabled>{t("selectPaymentMethod")}</option>
                        <option value="tipsterpage">{t("tipsterpage")}</option>
                        <option value="crypto">{t("crypto")}</option>
                        <option value="revolut">{t("revolut")}</option>
                        <option value="bizum">{t("bizum")}</option>
                        <option value="cash">{t("cash")}</option>
                        <option value="paypal">{t("paypal")}</option>
                        <option value="skrill">{t("skrill")}</option>
                        <option value="bankTransfer">{t("bankTransfer")}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1 text-white">{t("reference")} ({t("optional")})</label>
                      <input
                        type="text"
                        placeholder={t("enterReference")}
                        value={paymentReference}
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                        onChange={(e) => setPaymentReference(e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1 text-white">{t("comment")} ({t("optional")})</label>
                      <input
                        type="text"
                        placeholder={t("enterComment")}
                        value={paymentComment}
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                        onChange={(e) => setPaymentComment(e.target.value)}
                      />
                    </div>
                  </>
                )}

                <div className="flex justify-end pt-4">
                  <button
                    onClick={handleAddPayment}
                    disabled={!paymentAmount || (!isUserPage && (!paymentPercentage || !paymentMethod))}
                    className="px-4 py-2 bg-[#E8FC8B] text-[#1B2D1A] hover:bg-[#E8FC8B]/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-md flex items-center space-x-2"
                  >
                    <PlusCircleIcon size={16} />
                    <span>{t("add")}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <Modal show={showDeleteConfirmation} onHide={handleDeleteCancel} centered>
          <div className="bg-[#192619] text-[#ABCDDF] rounded-lg">
            <Modal.Header className="border-b border-[#2A3C29]">
              <Modal.Title className="text-[#ABCDDF]">{t('deletePayment')}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-[#192619]">
              <p className="text-[#ABCDDF]">{t('confirmDeletePayment')}</p>
            </Modal.Body>
            <Modal.Footer className="border-t border-[#2A3C29] bg-[#192619] d-flex justify-content-end gap-3">
              <button 
                onClick={handleDeleteCancel}
                className="px-4 py-2 bg-[#2A3C29] text-[#ABCDDF] hover:bg-[#3A4C39] rounded-md transition-colors duration-200"
              >
                {t('cancel')}
              </button>
              <button 
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-transparent border border-[#FF6B6B] text-[#FF6B6B] hover:bg-[#FF6B6B] hover:text-[#1B2D1A] rounded-md transition-all duration-200"
              >
                {t('delete')}
              </button>
            </Modal.Footer>
          </div>
        </Modal>
      )}
    </>
  );
};

export default PaymentModal; 